import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import PropTypes from "prop-types";

import Button from "@cayuse/shared-react.components.button";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";

import Certification from "components/Certification";
import CompleteReview from "components/CompleteReview";
import FormSummary from "components/FormSummary";
import StatusDropdown from "components/FormSummary/StatusDropdown";
import SPLink from "components/SPLink";
import StatusCommentModal from "components/StatusCommentModal";
import {
	API_PROPOSAL_FEED,
	AWARDS_BASE_ROUTE,
	COLUMN_HEADER_KEYS,
	DEFAULT_PROPOSAL_COLUMN_HEADERS,
	FORM_ACCESS_LEVELS,
	IS_DEV,
	PROJECTS_BASE_ROUTE,
	PROJECT_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
} from "globals/constants";
import {
	useCertificationText,
	useColumnHeaders,
	useDictionary,
	useFormAccesses,
	useHasRoles,
	useResolutions,
} from "hooks";
import store from "store/store";
import { getProposal, getProposalValues } from "store/actions/proposal";
import { completeCertificationProposalTask } from "store/actions/tasks/completeCertificationTask";
import {
	formSelectors,
	proposalSelectors,
	taskListSelectors,
	tenantSelectors,
	userAccessesSelectors,
} from "store/selectors";
import {
	filterProposalCertificationTasks,
	filterProposalReviewTasks,
	notifyError,
} from "utils";
import { api } from "services";

import styles from "./styles.css";


const DEFAULT_ACTIVE_COLUMN_HEADERS = [
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PI,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_START_DATE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.ADMIN_UNIT,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PRIME_SPONSOR,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.PROJECT_END_DATE,
	{ isPlaceholder: true },
	DEFAULT_PROPOSAL_COLUMN_HEADERS.SPONSOR_DEADLINE,
	DEFAULT_PROPOSAL_COLUMN_HEADERS.INSTRUMENT_TYPE,
	{ isPlaceholder: true },
	DEFAULT_PROPOSAL_COLUMN_HEADERS.TOTAL_SPONSOR_COSTS
];

const ProposalSummary = ({ isFormComplete }) => {
	const { defaultColumnHeaders } = useColumnHeaders(COLUMN_HEADER_KEYS.PROPOSAL, {
		defaultColumnHeaders: DEFAULT_ACTIVE_COLUMN_HEADERS
	});

	const { savingIndicator: isFormSaving, isEditMode, isFileUploading } = useSelector(formSelectors.entity);
	const { proposalStatuses } = useSelector(tenantSelectors.tenant);
	const { isFetching: isProposalValuesFetching, data: proposalValues } = useSelector(proposalSelectors.values);
	const proposal = useSelector(proposalSelectors.proposal);
	const { items: proposalTasks } = useSelector(taskListSelectors.proposalTasks);
	const currentProjectAccessTypes = useSelector(userAccessesSelectors.currentProjectAccessTypes);
	let { proposalId } = useParams();

	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isSpProposalAdmin,
		[PROJECT_PROPOSAL_ADMINISTRATOR]: isProposalAdmin,
	} = useHasRoles([
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_PROPOSAL_ADMINISTRATOR,
		PROJECT_PROPOSAL_ADMINISTRATOR,
	]);

	const [ resolutionOptions, setResolutionOptions ] = useState([]);
	const [ resolutionSelected, setResolutionSelected ] = useState(null);
	const [ resolutionsVisible, setResolutionsVisible ] = useState(false);

	const [ statusOptions, setStatusOptions ] = useState([]);
	const [ statusOverrides, setStatusOverrides ] = useState([]);
	const [ statusSelected, setStatusSelected ] = useState(null);
	const [ statusOverrideId, setStatusOverrideId ] = useState(null);

	const [ commentModalVisible, setCommentModalVisible ] = useState(false);
	const [ commentModalTitle, setCommentModalTitle ] = useState("");
	const [ comment, setComment ] = useState("");
	const [ isLoading, setIsLoading ] = useState(false);
	const [ isStatusLoading, setIsStatusLoading ] = useState(false);

	const dictionary = useDictionary("proposals.summary");
	const resolutions = useResolutions(COLUMN_HEADER_KEYS.PROPOSAL);
	const certificationTextData = useCertificationText(COLUMN_HEADER_KEYS.PROPOSAL);
	const formAccesses = useFormAccesses("proposal", proposal.proposalId);

	const canRoute = useMemo(
		() => isSystemAdmin || isSpProposalAdmin || isProposalAdmin
			|| currentProjectAccessTypes.some(access => FORM_ACCESS_LEVELS.PROPOSAL.ADMIN.includes(access)),
		[ isSystemAdmin, isSpProposalAdmin, isProposalAdmin, currentProjectAccessTypes ],
	);

	const statusDropdownDisabled = !formAccesses.changeStatus || !isNonEmptyArray(statusOverrides)
		|| isFormSaving || isEditMode || isFileUploading || isLoading;

	const isCertifiable = proposal.statusName === "Under Review";

	const onCommentOpen = (fromStatus, toStatus, toStatusId) => {
		const isResolutionVisible = (toStatus === "Closed");

		setCommentModalTitle(`Change Proposal Status: ${fromStatus} to ${toStatus}`);
		setStatusOverrideId(toStatusId);
		setComment("");
		setResolutionSelected(null);
		setResolutionsVisible(isResolutionVisible);
		setCommentModalVisible(true);
		setStatusSelected(toStatus);
	};

	const onCommentClose = (actionType = "close") => {
		setCommentModalVisible(false);
		actionType !== "confirm" && setStatusSelected(proposal.statusName);
	};

	const onConfirm = async () => {
		setIsStatusLoading(true);

		try {
			await api.proposal.updateStatus(
				{ id: proposal.proposalId },
				{
					id: statusOverrideId,
					comment: comment,
					resolution: resolutionSelected,
				},
			);

			onCommentClose("confirm");
			getProposal(proposal.proposalId);
			await loadStatus(proposal.proposalId);

		} catch (error) {
			const message = IS_DEV ? error.message : dictionary.completeReview.errors.reviewNotCompleted;
			notifyError(message, error);

		} finally {
			setIsStatusLoading(false);
		}
	};

	const routeForReview = async () => {
		setIsLoading(true);

		try {
			const id = proposal?.proposalId;
			if (!id) {
				throw new Error("Proposal ID is missed. Proposal cannot be routed for review.");
			}
			await api.proposal.routeForReview({ id: id });
			await getProposal(id, { isRefresh: true });
			await loadStatus(id);

		} catch (error) {
			console.error(error);
			const message = IS_DEV ? error.message : store.getState().dictionary.proposals.routing.errors.routeReviewFailed;
			notifyError(message, error);
		} finally {
			setIsLoading(false);
		}
	};

	const linkSection = useMemo( () => {
		const items = [];

		const link = ({ label, url, text }) => {
			return label === "Budget" ?
				<>
					<span className={styles.label}>{label}:</span>
					<a href={url} aria-label={`Go to ${label}: ${text}`}>
						{text}
					</a>
				</> :
				<>
					<span className={styles.label}>{label}:</span>
					<SPLink to={url} aria-label={`Go to ${label}: ${text}`}>
						{text}
					</SPLink>
				</>;
		};

		if(proposal) {
			const { projectId, projectNumber, summary: awards } = proposal;

			items.push(
				link({ label: "Project", url: `${PROJECTS_BASE_ROUTE}/${projectId}`, text: projectNumber ? projectNumber : "" })
			);

			// TODO: Talk to BE about getting the proposals children added to summary.
			if(isNonEmptyArray(awards)) {
				const awardsCopy = awards.slice(0, 3);

				awardsCopy.forEach(award => {
					items.push(
						link({ label: "Award", url: `${AWARDS_BASE_ROUTE}/${award.awardId}`, text: award.awardNumber }),
					);
				});
			}
		}

		if(isNonEmptyArray(items)) {
			return (
				<ul className={styles.summaryLinkList}>
					{items.map((item, index) => item ? <li key={`summary-link-${index}`} className={styles.summaryRow}>{item}</li> : "")}
				</ul>
			);
		}
	}, [ proposal ]);

	useEffect(() => {
		if (proposal && isNonEmptyString(proposal.statusName)) {
			setStatusSelected(proposal.statusName);
		}
	}, [ proposal ]);

	useEffect(() => {
		//TODO: need to chech why first time old "proposalId" is here
		if (isNonEmptyString(proposal.proposalId) && !isProposalValuesFetching && proposalId === proposal.proposalId) {
			getProposalValues({
				proposalId: proposal.proposalId,
				fieldKeys: []
			});
		}
	}, [ proposal.proposalId ]);

	const loadStatus = useCallback(async (id) => {
		try {
			const data = await api.proposal.getStatus({ id });

			setStatusOverrides(data);

		} catch (error) {
			notifyError("Something went wrong. Please try again.", error);
		}
	}, []);

	useEffect(() => {
		if (isNonEmptyString(proposal.proposalId)) {
			loadStatus(proposal.proposalId);
		}
	}, [ proposal.proposalId, canRoute ]);

	useEffect(() => {
		if (isNonEmptyArray(proposalStatuses)) {
			const options = proposalStatuses.map(item => {
				const disabled = !statusOverrides.some(override => item.description === override.description);
				const toStatus = item.description;
				const toStatusId = item.id;

				return {
					className: styles.statusOverride,
					key: item.id,
					value: item.description,
					text: item.description,
					disabled: disabled,
					onClick: () => !disabled && onCommentOpen(statusSelected, toStatus, toStatusId)
				};
			});

			setStatusOptions(options);
		}
	}, [ proposal, proposalStatuses, statusOverrides, statusSelected ]);

	useEffect(() => {
		if (resolutions) {
			const options = resolutions
				.filter(resolution => resolution.active)
				.sort((a, b) => (a.value > b.value) ? 1 : -1)
				.map(item => {
					return {
						key: item.value,
						value: item.value,
						label: item.value
					};
				});

			setResolutionOptions(options);
		}
	}, [ resolutions ]);

	const isUnderReview = proposal.statusName === "Under Review";

	const decisionOptions = [
		{
			key: "approved",
			value: "approved",
			text: dictionary.completeReview.decisions?.approved || "Approve"
		},
		{
			key: "declined",
			value: "declined",
			text: dictionary.completeReview.decisions?.declined || "Return to In Development"
		}
	];

	const actionButtons = [
		<CompleteReview key="CompleteReview"
			formInstance={proposal}
			instanceId={proposal.proposalId}
			instanceTasks={proposalTasks}
			instanceFeedEndpoint = {API_PROPOSAL_FEED}
			dictionary={dictionary.completeReview}
			getInstance={getProposal}
			filterInstanceReviewTasks={filterProposalReviewTasks}
			isCompleteReviewActive={isUnderReview}
			decisionOptions={decisionOptions}
		/>,
		<Certification  key="Certification"
			formInstance={proposal}
			instanceId={proposal.proposalId}
			instanceTasks={proposalTasks}
			getInstance={getProposal}
			dictionary={dictionary.certification}
			isCertifiable ={isCertifiable}
			certificationTextData ={certificationTextData}
			completeCertificationTask={completeCertificationProposalTask}
			filterInstanceCertificationTasks={filterProposalCertificationTasks}
		/>,
		<Button key="RouteForReview"
			disabled={(statusSelected !== "In Development") || !isFormComplete || !canRoute || isLoading || isFormSaving || isFileUploading}
			busy={isLoading}
			loadingMessage="Save in process"
			onClick={routeForReview}
		>
			{dictionary.routeForReview || "Route for Review"}
		</Button>
	];

	const formInstance = {
		instance: proposal,
		instanceId: proposal.proposalId,
		values: proposalValues
	};

	return (
		<div id="proposalSummary">
			<StatusCommentModal
				comment={comment}
				title={commentModalTitle}
				resolutions={resolutionOptions}
				resolutionSelected={resolutionSelected}
				resolutionsVisible={resolutionsVisible}
				visible={commentModalVisible}
				onClose={onCommentClose}
				onConfirm={onConfirm}
				onCommentChange={value => setComment(value)}
				onResolutionChange={value => setResolutionSelected(value)}
				isLoading={isStatusLoading}
				formType = {COLUMN_HEADER_KEYS.PROPOSAL}
			/>
			<FormSummary
				summaryTitle={ dictionary.proposalSummary || "Proposal Summary" }
				actionButtons={ actionButtons }
				statusDropdown={
					<StatusDropdown
						data-testid="status-dropdown"
						options={statusOptions}
						value={statusSelected}
						disabled={statusDropdownDisabled}
						ariaLabel="Select proposal status"
						label="Proposal status:"
					/>
				}
				defaultColumnHeaders={ defaultColumnHeaders }
				formInstance={ formInstance }
				formType={COLUMN_HEADER_KEYS.PROPOSAL}
				thirdColumnContent={linkSection}
			/>
		</div>
	);
};

ProposalSummary.propTypes = {
	isFormComplete: PropTypes.bool,
};

export default ProposalSummary;
